import React from 'react';
import { Helmet } from 'react-helmet-async';
import { ArrowRight } from 'lucide-react';
import { Link } from 'react-router-dom';
import Header from '../components/Header';
import Breadcrumb from '../components/Breadcrumb';
import Footer from '../components/Footer';
import {
  BRAND_NAME,
  COMPLIANCE_EMAIL,
  MAILING_ADDRESS_LINES,
} from '../config/legal';

const breadcrumbItems = [
  { label: 'Home', href: '/' },
  { label: 'Privacy Policy', href: '/privacy-policy', current: true },
];

const PrivacyPolicyPage = () => {
  return (
    <div className="min-h-screen" style={{ backgroundColor: '#1F3B60' }}>
      <Helmet>
        <title>Privacy Policy | {BRAND_NAME}</title>
        <meta name="description" content={`Privacy Policy for ${BRAND_NAME}.`} />
        <link rel="canonical" href="https://pacificcrestprop.com/privacy-policy" />
        <meta property="og:title" content={`Privacy Policy | ${BRAND_NAME}`} />
        <meta property="og:description" content={`Privacy Policy for ${BRAND_NAME}.`} />
        <meta property="og:url" content="https://pacificcrestprop.com/privacy-policy" />
      </Helmet>

      <Header />
      <div className="pt-20">
        <Breadcrumb items={breadcrumbItems} variant="dark" />
      </div>

      {/* Hero Section */}
      <section className="pt-8 pb-12 px-4 sm:px-6 lg:px-8">
        <div className="max-w-4xl mx-auto text-center">
          <h1 className="font-display font-bold text-4xl sm:text-5xl text-white mb-6">
            Privacy Policy &ndash; {BRAND_NAME}
          </h1>
          <div className="divider-gold mx-auto mb-8" />
          <p className="text-lg text-white/70">Effective Date: February 2026</p>
        </div>
      </section>

      {/* Content */}
      <section className="pb-16 px-4 sm:px-6 lg:px-8" style={{ backgroundColor: '#F4F1E9' }}>
        <div className="max-w-4xl mx-auto py-16">
          <div className="prose prose-lg max-w-none" style={{ color: '#1F3B60' }}>

            <p>
              {BRAND_NAME} ("Company," "we," "us," or "our") provides real estate solutions in Los Angeles.
            </p>

            <p>
              This Privacy Policy explains how we collect, use, disclose, and protect information submitted through our websites, including:
            </p>
            <ul className="list-disc pl-6 space-y-1">
              <li>pacificcrestprop.com</li>
              <li>offer.pacificcrestprop.com</li>
              <li>renovation.pacificcrestprop.com</li>
            </ul>

            {/* 1 */}
            <h2 className="font-display font-bold text-2xl mt-10 mb-4" style={{ color: '#1F3B60' }}>
              1. Information We Collect
            </h2>
            <p>When you submit a form or contact us, we may collect:</p>
            <ul className="list-disc pl-6 space-y-1">
              <li>Name</li>
              <li>Phone number</li>
              <li>Email address</li>
              <li>Property address</li>
              <li>Property details</li>
              <li>Communications (including SMS opt-in)</li>
            </ul>
            <p>
              We may also collect technical data such as IP address, browser type, device type, and basic analytics data for fraud prevention and site performance.
            </p>

            {/* 2 */}
            <h2 className="font-display font-bold text-2xl mt-10 mb-4" style={{ color: '#1F3B60' }}>
              2. How We Use Your Information
            </h2>
            <p>We use collected information to:</p>
            <ul className="list-disc pl-6 space-y-1">
              <li>Respond to property inquiries</li>
              <li>Provide offers or renovation quotes</li>
              <li>Schedule appointments</li>
              <li>Coordinate transactions</li>
              <li>Improve website functionality</li>
              <li>Maintain compliance and consent records</li>
              <li>Communicate via phone, email, or SMS (if you opt in)</li>
            </ul>
            <p>We do not sell your personal information.</p>

            {/* 3 */}
            <h2 className="font-display font-bold text-2xl mt-10 mb-4" style={{ color: '#1F3B60' }}>
              3. SMS Communications &amp; TCPA Compliance
            </h2>
            <p>If you affirmatively opt in by checking the SMS consent box, you consent to receive text messages from {BRAND_NAME}.</p>
            <p>Messages may include:</p>
            <ul className="list-disc pl-6 space-y-1">
              <li>Property offers</li>
              <li>Scheduling coordination</li>
              <li>Follow-ups regarding your inquiry</li>
              <li>Transaction-related updates</li>
            </ul>
            <p>Message frequency varies.</p>
            <p>Message and data rates may apply.</p>
            <p>You may opt out at any time by replying STOP.</p>
            <p>For assistance, reply HELP.</p>
            <p>Consent to receive SMS is not a condition of purchase.</p>
            <p>
              We may retain consent logs including timestamp, IP address, form source URL, and disclosure version for compliance purposes.
            </p>
            <p>SMS consent is not shared with third parties for their marketing purposes.</p>

            {/* 4 */}
            <h2 className="font-display font-bold text-2xl mt-10 mb-4" style={{ color: '#1F3B60' }}>
              4. Information Sharing
            </h2>
            <p>We may share information with trusted service providers that assist in operating our business, including:</p>
            <ul className="list-disc pl-6 space-y-1">
              <li>Communications providers (e.g., phone and SMS platforms)</li>
              <li>CRM and lead management platforms</li>
              <li>Website hosting providers</li>
              <li>Analytics providers</li>
              <li>Contractors involved in property services</li>
            </ul>
            <p>These providers are required to safeguard your information and use it only for authorized purposes.</p>
            <p>No mobile information will be shared with third parties/affiliates for marketing/promotional purposes. All the above categories exclude text messaging originator opt-in data and consent; this information will not be shared with any third parties.</p>

            {/* 5 */}
            <h2 className="font-display font-bold text-2xl mt-10 mb-4" style={{ color: '#1F3B60' }}>
              5. Cookies &amp; Analytics
            </h2>
            <p>
              We may use cookies and similar technologies to improve user experience and analyze website performance. You may control cookies via your browser settings.
            </p>

            {/* 6 */}
            <h2 className="font-display font-bold text-2xl mt-10 mb-4" style={{ color: '#1F3B60' }}>
              6. Data Retention
            </h2>
            <p>We retain personal information as reasonably necessary to:</p>
            <ul className="list-disc pl-6 space-y-1">
              <li>Provide services</li>
              <li>Maintain business records</li>
              <li>Satisfy legal, regulatory, and carrier compliance obligations</li>
            </ul>

            {/* 7 */}
            <h2 className="font-display font-bold text-2xl mt-10 mb-4" style={{ color: '#1F3B60' }}>
              7. Data Security
            </h2>
            <p>
              We implement reasonable administrative, technical, and physical safeguards to protect your data. However, no method of transmission or storage is completely secure.
            </p>

            {/* 8 */}
            <h2 className="font-display font-bold text-2xl mt-10 mb-4" style={{ color: '#1F3B60' }}>
              8. Your Rights
            </h2>
            <p>You may request to:</p>
            <ul className="list-disc pl-6 space-y-1">
              <li>Access your personal data</li>
              <li>Correct inaccurate data</li>
              <li>Request deletion of data (where applicable)</li>
              <li>Opt out of SMS by replying STOP</li>
            </ul>
            <p>Requests may be sent to:</p>
            <p>
              <a href={`mailto:${COMPLIANCE_EMAIL}`} className="underline" style={{ color: '#D6A94F' }}>
                {COMPLIANCE_EMAIL}
              </a>
            </p>

            {/* 9 */}
            <h2 className="font-display font-bold text-2xl mt-10 mb-4" style={{ color: '#1F3B60' }}>
              9. Contact Information
            </h2>
            <p>{BRAND_NAME}</p>
            <div className="mt-4">
              {MAILING_ADDRESS_LINES.map((line, i) => (
                <p key={i} className="my-0">{line}</p>
              ))}
            </div>
            <p className="mt-4">
              <a href={`mailto:${COMPLIANCE_EMAIL}`} className="underline" style={{ color: '#D6A94F' }}>
                {COMPLIANCE_EMAIL}
              </a>
            </p>
            <p>
              <a href="https://pacificcrestprop.com" className="underline" style={{ color: '#D6A94F' }}>
                https://pacificcrestprop.com
              </a>
            </p>

            {/* 10 */}
            <h2 className="font-display font-bold text-2xl mt-10 mb-4" style={{ color: '#1F3B60' }}>
              10. Policy Updates
            </h2>
            <p>
              We may update this Privacy Policy periodically. Updates will be posted on this page with a revised effective date.
            </p>
          </div>
        </div>
      </section>

      {/* Back to Home */}
      <section className="py-12 px-4 text-center" style={{ backgroundColor: '#1F3B60' }}>
        <Link
          to="/"
          className="inline-flex items-center space-x-2 font-medium text-white/60 hover:text-white transition-colors duration-300"
        >
          <ArrowRight className="w-4 h-4 rotate-180" />
          <span>Back to Home</span>
        </Link>
      </section>

      <Footer />
    </div>
  );
};

export default PrivacyPolicyPage;
